import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { ProblemAnswer } from '@yandex-int/k-common/typings';

import { MarkerAnswer, ProblemAnswerItem, ProblemsViewStore } from './typings';

const initialState: ProblemsViewStore = {
    problems: {},
    selectedProblemId: null,
    shouldShowCorrectAnswers: false,
};

const getInitialProblemAnswerState = (): ProblemAnswerItem => ({
    answers: {},
    attempt: {} as ProblemAnswer,
    loadingStatus: LOADING_STATUSES.UNSENT,
});

const slice = createSlice({
    name: 'PROBLEMS_VIEW',
    initialState,
    reducers: {
        setSelectedProblemId: (state, action: PayloadAction<number | null>) => {
            state.selectedProblemId = action.payload;
        },

        toggleProblemCorrectAnswer: (state, action: PayloadAction<boolean>) => {
            state.shouldShowCorrectAnswers = action.payload;
        },

        postAnswerRequest: (
            state,
            action: PayloadAction<{
                answer: any;
                problemId: number;
            }>
        ) => {
            const { problemId } = action.payload;

            setInitialState(state, problemId);

            state.problems[problemId].loadingStatus = LOADING_STATUSES.LOADING;
        },

        postAnswerSuccess: (
            state,
            action: PayloadAction<{
                attempt: any;
                problemId: number;
            }>
        ) => {
            const { attempt, problemId } = action.payload;

            setInitialState(state, problemId);

            state.problems[problemId].loadingStatus = LOADING_STATUSES.SUCCESS;
            state.problems[problemId].attempt = attempt;
        },

        postAnswerError: (
            state,
            action: PayloadAction<{
                problemId: number;
            }>
        ) => {
            const { problemId } = action.payload;

            state.problems[problemId].loadingStatus = LOADING_STATUSES.ERROR;
        },

        setMarkerAnswer: (
            state,
            action: PayloadAction<{
                answer: MarkerAnswer;
                markerId: number;
                problemId: number;
            }>
        ) => {
            const { answer, markerId, problemId } = action.payload;

            setInitialState(state, problemId);

            state.problems[problemId].answers[markerId] = answer;
        },

        clearProblemAnswer: (
            state,
            action: PayloadAction<{
                problemId: number;
            }>
        ) => {
            const { problemId } = action.payload;

            state.problems[problemId] = getInitialProblemAnswerState();
        },

        clearAllProblemsAnswers: (state) => {
            state.problems = initialState.problems;
        },
    },
});

const setInitialState = (state: ProblemsViewStore, problemId: number) => {
    if (!state.problems[problemId]) {
        state.problems[problemId] = getInitialProblemAnswerState();
    }
};

export const actions = slice.actions;
export const reducer = slice.reducer;
